<template>
    <div>
        <div v-if="uploadedUrl" class="flex flex-col items-center gap-3">
            <img
                :src="uploadedUrl"
                :alt="defaultAlt || 'imagen'"
                class="rounded-xl border border-theme-border object-contain bg-theme-canvas"
                :style="{
                    width: previewWidth + 'px',
                    height: previewHeight + 'px'
                }"
            />
            <app-button border icon @click="triggerFileInput"> ✏️ </app-button>
        </div>

        <div v-else class="flex justify-center">
            <button
                class="rounded-xl border-2 border-dashed border-theme-primary gap-1.5 text-theme-muted hover:border-stack-primary hover:text-stack-primary transition-colors cursor-pointer"
                :style="{
                    width: previewWidth + 'px',
                    height: previewHeight + 'px'
                }"
                :disabled="isLoading"
                @click="triggerFileInput"
            >
                <div
                    v-if="!isLoading"
                    class="flex flex-col items-center justify-center"
                >
                    <span class="text-2xl">🖼</span>
                    <span class="text-xs font-medium">{{
                        defaultAlt || "Seleccionar imagen"
                    }}</span>
                </div>
                <div
                    v-else
                    class="flex flex-col items-center justify-center py-6 text-theme-muted"
                >
                    <svg
                        class="w-8 h-8 animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="#E5E7EB"
                            stroke-width="4"
                            fill="none"
                        />

                        <path
                            d="M22 12A10 10 0 0 0 12 2"
                            stroke="#D32F2F"
                            stroke-width="4"
                            stroke-linecap="round"
                            fill="none"
                        />
                    </svg>

                    <span class="mt-3 text-sm">Loading...</span>
                </div>
            </button>
        </div>

        <input
            ref="fileInput"
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            class="hidden"
            @input="onFileSelected"
        />

        <image-rights-notice v-if="!hideImageRightsNotice" class="mt-2" />

        <p v-if="error" class="text-theme-danger text-sm text-center mt-2">
            {{ error }}
        </p>
    </div>
</template>

<script>
import { mapActions } from "vuex";

import {
    SVG_NOT_ALLOWED_MESSAGE,
    isAllowedImageFile
} from "@/common/lib/image-files";

import ImageRightsNotice from "@/components/utils/ImageRightsNotice.vue";

export default {
    name: "ImageUploader",
    components: { ImageRightsNotice },
    props: {
        initialUrl: {
            type: String,
            default: ""
        },
        defaultAlt: {
            type: String,
            default: ""
        },
        defaultSize: {
            type: String,
            default: "customImage"
        },
        previewWidth: {
            type: Number,
            default: 200
        },
        previewHeight: {
            type: Number,
            default: 80
        },
        hideImageRightsNotice: {
            type: Boolean,
            default: false
        }
    },
    emits: ["updated", "loading"],
    data: () => ({
        uploadedUrl: "",
        isLoading: false,
        error: ""
    }),
    watch: {
        initialUrl: {
            immediate: true,
            handler(url) {
                if (url && !this.uploadedUrl) {
                    this.uploadedUrl = url;
                }
            }
        }
    },
    methods: {
        ...mapActions("images", [
            "getEndPointUploadImage",
            "uploadImage",
            "createImage"
        ]),
        triggerFileInput() {
            this.$refs.fileInput.value = "";
            this.$refs.fileInput.click();
        },
        async onFileSelected(event) {
            const files = event.target.files || event.dataTransfer.files;
            if (!files.length) return;

            const file = files[0];
            if (!isAllowedImageFile(file)) {
                this.error = SVG_NOT_ALLOWED_MESSAGE;
                return;
            }
            const fileSizeMB = file.size / 1048576;
            if (fileSizeMB > 20) {
                this.error = `Archivo demasiado pesado: ${fileSizeMB.toFixed(
                    1
                )} MB`;
                return;
            }

            this.error = "";
            this.isLoading = true;
            this.$emit("loading", true);

            try {
                const { id, url, urlSigned } =
                    await this.getEndPointUploadImage({
                        fileName: file.name,
                        fileSize: file.size,
                        alt: this.defaultAlt,
                        size: this.defaultSize
                    });

                await this.uploadImage({ url: urlSigned, file });

                await this.createImage({
                    id,
                    url,
                    alt: this.defaultAlt,
                    size: this.defaultSize
                });

                this.uploadedUrl = url;
                this.$emit("updated", url);

                // Esperar 10 segundos antes de quitar el loading
                await new Promise((resolve) => setTimeout(resolve, 10000));
                this.isLoading = false;
                this.$emit("loading", false);
            } catch (err) {
                this.isLoading = false;
                this.$emit("loading", false);
                this.error = err.message || "Error al subir la imagen";
            }
        }
    }
};
</script>
